export interface RetryOptions {
  attempts?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  signal?: AbortSignal;
  onRetry?: (attempt: number, err: unknown) => void;
}

// Exponential backoff with full jitter. Used for S3 part PUTs, where transient
// 5xx and network blips are expected at scale and a fixed retry would either
// thunder back instantly or wait too long.
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const attempts = options.attempts ?? 4;
  const base = options.baseDelayMs ?? 500;
  const max = options.maxDelayMs ?? 8000;

  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    if (options.signal?.aborted) throw new DOMException('aborted', 'AbortError');
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (i === attempts - 1) break;
      options.onRetry?.(i + 1, err);
      const exp = Math.min(max, base * 2 ** i);
      const delay = Math.floor(Math.random() * exp);
      await sleep(delay, options.signal);
    }
  }
  throw lastErr;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(t);
      reject(new DOMException('aborted', 'AbortError'));
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

// Run jobs with at most `concurrency` in flight at a time. Preserves input
// order in the returned array. Throws on first failure (caller decides what
// to do with partial state — for upload it's surfaced via per-file status).
export async function runWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  async function loop() {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await worker(items[i]!, i);
    }
  }
  const lanes = Array.from({ length: Math.min(concurrency, items.length) }, () => loop());
  await Promise.all(lanes);
  return results;
}
